import React from 'react';
import { Dialog } from '../reusable';
import ProductionStatusChip from './ProductionStatusChip';
import { MATERIAL_STATUS_COLORS, STOCK_STATUS_COLORS, formatMoneyClp, inventoryValueOf, stockFromLots, stockStatus } from './rawMaterialsDomain';
import { formatDateEs, formatQty } from './productionDomain';

const Field = ({ label, value, muted }) => (
  <div>
    <div style={{ fontSize: 11, fontWeight: 600, color: muted, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
      {label}
    </div>
    <div style={{ marginTop: 2, fontSize: 14, wordBreak: 'break-word' }}>{value || '—'}</div>
  </div>
);

const PaintMaterialDetailModal = ({
  isOpen,
  onClose,
  material,
  lots = [],
  isDarkMode = false,
  ui,
  onEdit,
  onReceive,
}) => {
  if (!material) return null;
  const muted = ui?.muted || (isDarkMode ? '#a1a1aa' : '#64748b');
  const text = ui?.text || (isDarkMode ? '#f4f4f5' : '#0f172a');
  const border = ui?.border || (isDarkMode ? '#3f3f46' : '#e2e8f0');
  const qty = stockFromLots(lots, material.code);
  const status = stockStatus(qty, material.min_stock);

  return (
    <Dialog
      isOpen={isOpen}
      onClose={onClose}
      title={material.code}
      subtitle={
        material.procurement_code
          ? `${material.name} · Adq. ${material.procurement_code}`
          : material.name
      }
      size="lg"
      isDarkMode={isDarkMode}
    >
      <div style={{ padding: '0 20px 20px', color: text }}>
        <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap' }}>
          <ProductionStatusChip status={material.status} color={MATERIAL_STATUS_COLORS[material.status]} />
          <ProductionStatusChip status={status} color={STOCK_STATUS_COLORS[status]} />
          <ProductionStatusChip status={material.family} color="#2563eb" />
          {material.has_sds ? <ProductionStatusChip status="Hoja SDS" color="#0f766e" /> : null}
        </div>

        <h4 style={{ margin: '0 0 10px', fontSize: 13 }}>Identificación</h4>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: 14, marginBottom: 18 }}>
          <Field label="Categoría" value={material.family} muted={muted} />
          <Field label="Código producción" value={material.code} muted={muted} />
          <Field
            label="Código adquisiciones"
            value={
              material.procurement_code
                ? `${material.procurement_code}${material.procurement_name ? ` · ${material.procurement_name}` : ''}`
                : '—'
            }
            muted={muted}
          />
          <Field label="Nombre comercial" value={material.name} muted={muted} />
          <Field label="Nombre químico" value={material.chemical_name} muted={muted} />
          <Field label="N° CAS" value={material.cas_number} muted={muted} />
          <Field label="Apariencia" value={material.appearance} muted={muted} />
          <Field label="Clase de peligro" value={material.hazard_class} muted={muted} />
          <Field label="Estado" value={material.status} muted={muted} />
        </div>

        <h4 style={{ margin: '0 0 10px', fontSize: 13 }}>Parámetros técnicos</h4>
        <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: 18 }}>
          <thead>
            <tr style={{ borderBottom: `1px solid ${border}` }}>
              {['Parámetro', 'Unidad', 'Valor ideal', 'Mínimo', 'Máximo'].map((header) => (
                <th key={header} style={{ textAlign: 'left', padding: '8px 10px', fontSize: 11, color: muted }}>
                  {header}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {(material.tech_params || []).length ? (
              material.tech_params.map((param) => (
                <tr key={param.key || param.name} style={{ borderBottom: `1px solid ${border}` }}>
                  <td style={{ padding: '8px 10px', fontSize: 13 }}>{param.name}</td>
                  <td style={{ padding: '8px 10px', fontSize: 13 }}>{param.unit || '—'}</td>
                  <td style={{ padding: '8px 10px', fontSize: 13 }}>{param.ideal === '' || param.ideal == null ? '—' : param.ideal}</td>
                  <td style={{ padding: '8px 10px', fontSize: 13 }}>{param.min === '' || param.min == null ? '—' : param.min}</td>
                  <td style={{ padding: '8px 10px', fontSize: 13 }}>{param.max === '' || param.max == null ? '—' : param.max}</td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={5} style={{ padding: 14, color: muted }}>
                  Sin parámetros técnicos.
                </td>
              </tr>
            )}
          </tbody>
        </table>

        <h4 style={{ margin: '0 0 10px', fontSize: 13 }}>Abastecimiento</h4>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: 14, marginBottom: 18 }}>
          <Field label="Unidad" value={material.unit} muted={muted} />
          <Field label="Costo unitario" value={material.unit_cost ? formatMoneyClp(material.unit_cost) : '—'} muted={muted} />
          <Field label="Valor inventario" value={formatMoneyClp(inventoryValueOf(qty, material))} muted={muted} />
          <Field label="Proveedor habitual" value={material.typical_supplier} muted={muted} />
          <Field label="Stock mínimo" value={formatQty(material.min_stock, material.unit)} muted={muted} />
          <Field label="Stock actual" value={formatQty(qty, material.unit)} muted={muted} />
          <Field label="Envase" value={material.packing} muted={muted} />
          <Field label="Almacenamiento" value={material.storage} muted={muted} />
        </div>
        {material.notes ? <p style={{ margin: '0 0 16px', fontSize: 13, color: muted }}>{material.notes}</p> : null}

        <h4 style={{ margin: '0 0 8px', fontSize: 13 }}>Lotes en bodega</h4>
        <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: 16 }}>
          <thead>
            <tr style={{ borderBottom: `1px solid ${border}` }}>
              {['Lote', 'Bodega', 'Cantidad', 'Recepción', 'Vence'].map((header) => (
                <th key={header} style={{ textAlign: 'left', padding: '8px 10px', fontSize: 11, color: muted }}>
                  {header}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {lots.length ? (
              lots.map((lot) => (
                <tr key={lot.id} style={{ borderBottom: `1px solid ${border}` }}>
                  <td style={{ padding: '8px 10px', fontSize: 13 }}>{lot.lot_code}</td>
                  <td style={{ padding: '8px 10px', fontSize: 13 }}>{lot.warehouse}</td>
                  <td style={{ padding: '8px 10px', fontSize: 13 }}>{formatQty(lot.quantity, lot.unit)}</td>
                  <td style={{ padding: '8px 10px', fontSize: 13 }}>{formatDateEs(lot.received_at)}</td>
                  <td style={{ padding: '8px 10px', fontSize: 13 }}>{formatDateEs(lot.expiry)}</td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={5} style={{ padding: 14, color: muted }}>
                  Sin lotes asociados.
                </td>
              </tr>
            )}
          </tbody>
        </table>

        {onEdit || onReceive ? (
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
            {onReceive ? (
              <button
                type="button"
                onClick={() => onReceive(material)}
                style={{
                  height: 34,
                  padding: '0 12px',
                  borderRadius: 8,
                  border: 'none',
                  background: '#16a34a',
                  color: '#fff',
                  fontWeight: 600,
                  cursor: 'pointer',
                }}
              >
                Recepcionar
              </button>
            ) : null}
            {onEdit ? (
            <button
              type="button"
              onClick={() => onEdit(material)}
              style={{
                height: 34,
                padding: '0 12px',
                borderRadius: 8,
                border: 'none',
                background: '#2563eb',
                color: '#fff',
                fontWeight: 600,
                cursor: 'pointer',
              }}
            >
              Editar ficha
            </button>
            ) : null}
          </div>
        ) : null}
      </div>
    </Dialog>
  );
};

export default PaintMaterialDetailModal;
